import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import Service from '@ember/service';
import config from 'garaje/config/environment';
import type CompanyModel from 'garaje/models/company';
import type LocationModel from 'garaje/models/location';
import type UserModel from 'garaje/models/user';
import type ZoneModel from 'garaje/models/zone';
import LogRocket from 'logrocket';

interface ContextOptions {
  currentCompany: CompanyModel;
  currentLocation?: LocationModel | null;
  currentUser: UserModel;
  currentZone?: ZoneModel | null;
}

export default class DatadogService extends Service {
  #loggerInitialized = false;
  #rumInitialized = false;

  constructor(owner: object) {
    super(owner);

    this.#initializeLogger();
    this.#initializeRum();
  }

  setContext({ currentCompany, currentLocation, currentUser, currentZone }: ContextOptions): void {
    const sdks: Array<typeof datadogLogs | typeof datadogRum> = [];
    if (this.#useLogger) {
      if (!this.#loggerInitialized) {
        this.#initializeLogger();
      }
      sdks.push(datadogLogs);
    }
    if (this.#useRum) {
      if (!this.#rumInitialized) {
        this.#initializeRum();
      }
      sdks.push(datadogRum);
    }

    for (const datadog of sdks) {
      datadog.setUser({ id: currentUser.id, name: currentUser.fullName });
      datadog.setGlobalContextProperty('company', { id: currentCompany.id, name: currentCompany.name });

      if (currentLocation) {
        datadog.setGlobalContextProperty('location', { id: currentLocation.id, name: currentLocation.name });
      } else {
        datadog.removeGlobalContextProperty('location');
      }

      if (currentZone) {
        datadog.setGlobalContextProperty('zone', { id: currentZone.id, name: currentZone.name });
      } else {
        datadog.removeGlobalContextProperty('zone');
      }
    }
  }

  reportError(error: Error): void {
    if (!this.#rumInitialized) return;

    datadogRum.addError(error);
  }

  logError(message: string, context: object, error?: Error): void {
    if (!this.#loggerInitialized) return;

    datadogLogs.logger.error(message, context, error);
  }

  logInfo(message: string, context: object): void {
    if (!this.#loggerInitialized) return;

    datadogLogs.logger.info(message, context);
  }

  logWarning(message: string, context: object, error?: Error): void {
    if (!this.#loggerInitialized) return;

    datadogLogs.logger.warn(message, context, error);
  }

  #initializeLogger(): void {
    if (!this.#useLogger) return;
    if (this.#loggerInitialized) return;

    if (!config.datadogConfig?.clientToken) {
      // eslint-disable-next-line no-console
      console.warn(
        'Datadog logging was requested to be enabled, but `clientToken` is missing\n. Please ensure that `ENV.datadogConfig.clientToken` is configured `config/environments/%s.js`.',
        config.environment,
      );
      return;
    }

    datadogLogs.init({
      clientToken: config.datadogConfig.clientToken,
      site: 'datadoghq.com',
      service: 'garaje',
      env: config.environment,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: [],
      sessionSampleRate: 100,
    });

    this.#loggerInitialized = true;

    LogRocket.getSessionURL((sessionURL) => {
      datadogLogs.setGlobalContextProperty('LogRocket', { urlAtTime: sessionURL });
    });
  }

  #initializeRum(): void {
    if (!this.#useRum) return;
    if (this.#rumInitialized) return;

    if (!config.datadogConfig?.applicationId || !config.datadogConfig?.clientToken) {
      // eslint-disable-next-line no-console
      console.warn(
        'Datadog RUM was requested to be enabled, but either `applicationId` or `clientToken` is missing\n. Please ensure that `ENV.datadogConfig.applicationId` and `ENV.datadogConfig.clientToken` are configured `config/environments/%s.js`.',
        config.environment,
      );
      return;
    }

    datadogRum.init({
      applicationId: config.datadogConfig.applicationId,
      clientToken: config.datadogConfig.clientToken,
      site: 'datadoghq.com',
      service: 'garaje',
      env: config.environment,
      version: config.datadogConfig.appVersion,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 0,
      trackUserInteractions: false,
      trackResources: false,
      trackLongTasks: false,
      defaultPrivacyLevel: 'mask-user-input',
    });

    this.#rumInitialized = true;

    LogRocket.getSessionURL((sessionURL) => {
      datadogRum.setGlobalContextProperty('LogRocket', { urlAtTime: sessionURL });
    });
  }

  get #useLogger(): boolean {
    if (typeof config.datadogConfig?.enabled === 'boolean') {
      return config.datadogConfig.enabled;
    } else if (typeof config.datadogConfig?.enabled === 'object') {
      return !!config.datadogConfig.enabled.logger;
    }
    return false;
  }

  get #useRum(): boolean {
    if (typeof config.datadogConfig?.enabled === 'boolean') {
      return config.datadogConfig.enabled;
    } else if (typeof config.datadogConfig?.enabled === 'object') {
      return !!config.datadogConfig.enabled.rum;
    }
    return false;
  }
}
